import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Loader2, ArrowRight, Sparkles } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const archetypeIconPaths = {
  "Innocent": "/icons/innocent.png",
  "Explorer": "/icons/explorer.png",
  "Sage": "/icons/sage.png",
  "Hero": "/icons/hero.png",
  "Rebel": "/icons/rebel.png",
  "Rebel": "/icons/rebel.png",
  "Magician": "/icons/magician.png",
  "Everyman": "/icons/everyman.png",
  "Lover": "/icons/lover.png",
  "Jester": "/icons/jester.png",
  "Caregiver": "/icons/caregiver.png",
  "Ruler": "/icons/ruler.png",
  "Creator": "/icons/creator.png"
};

export default function SharedResults() {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/share-results/${shareId}`);
        if (!res.ok) { setNotFound(true); return; }
        setData(await res.json());
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [shareId, API_URL]);

  if (loading) return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <Loader2 className="h-8 w-8 text-white animate-spin" />
    </div>
  );

  if (notFound) return (
    <div className="min-h-screen bg-black flex items-center justify-center px-4">
      <div className="text-center">
        <div className="text-6xl font-extrabold text-white mb-4">404</div>
        <div className="text-gray-400 mb-6">This shared result has expired or doesn't exist.</div>
        <Button onClick={() => navigate('/')} className="bg-[#0a0a0a] text-[#f5f5f5] font-bold">
          Take the Quiz
        </Button>
      </div>
    </div>
  );

  const primary = data.archetype_data?.primary || {};
  const secondary = data.archetype_data?.secondary || {};
  const primaryIcon = archetypeIconPaths[data.primary_archetype] || "/icons/everyman.png";
  const secondaryIcon = archetypeIconPaths[data.secondary_archetype] || "/icons/everyman.png";

  const InfoRow = ({ label, value }) => value ? (
    <div className="mb-4">
      <div className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-1">{label}</div>
      <div className="text-sm text-gray-200 leading-relaxed">{value}</div>
    </div>
  ) : null;

  return (
    <div className="min-h-screen flex flex-col bg-black">
      <Header />
      <main className="flex-1 bg-[#0a0a0a]">

        {/* Shared badge */}
        <div className="bg-black text-center py-3 px-4">
          <span className="text-xs text-gray-400 tracking-widest uppercase">
            {data.company_label ? `${data.company_label} · ` : ''}Shared Brand Archetype Results
          </span>
        </div>

        {/* Primary Archetype Hero */}
        <div className="bg-black pt-10 pb-12 px-4">
          <div className="max-w-3xl mx-auto text-center">
            <div className="text-xs text-[#9ca3af] font-bold uppercase tracking-widest mb-4">Primary Brand Archetype</div>
            {primaryIcon && (
              <img src={primaryIcon} alt={data.primary_archetype} className="w-20 h-20 object-contain mx-auto mb-5 opacity-90" />
            )}
            <h1 className="text-5xl sm:text-6xl font-extrabold text-white tracking-tight mb-4">
              {data.primary_archetype}
            </h1>
            {primary.description && (
              <p className="text-gray-300 text-base sm:text-lg max-w-xl mx-auto leading-relaxed mb-6">
                {primary.description}
              </p>
            )}
            {primary.coreTraits && (
              <div className="flex flex-wrap justify-center gap-2">
                {(Array.isArray(primary.coreTraits) ? primary.coreTraits : String(primary.coreTraits).split(',')).map((t) => (
                  <span key={`p-trait-${String(t).trim()}`} className="bg-[#0a0a0a]/10 text-white text-xs font-semibold px-3 py-1.5 rounded-full border border-white/20">
                    {String(t).trim()}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-4 py-10 space-y-8">

          {/* Secondary Archetype */}
          <div className="border-2 border-[#e5e5e5] p-6 sm:p-8">
            <div className="flex items-center gap-4 mb-4">
              {secondaryIcon && (
                <img src={secondaryIcon} alt={data.secondary_archetype} className="w-12 h-12 object-contain" />
              )}
              <div>
                <div className="text-xs text-[#9ca3af] font-bold uppercase tracking-widest">Secondary Archetype</div>
                <div className="text-2xl font-extrabold text-[#f5f5f5]">{data.secondary_archetype}</div>
              </div>
            </div>
            {secondary.description && (
              <p className="text-[#a3a3a3] text-sm leading-relaxed mb-3">{secondary.description}</p>
            )}
            {secondary.coreTraits && (
              <div className="flex flex-wrap gap-2">
                {(Array.isArray(secondary.coreTraits) ? secondary.coreTraits : String(secondary.coreTraits).split(',')).map((t) => (
                  <span key={`s-trait-${String(t).trim()}`} className="bg-[#1a1a1a] text-[#d4d4d4] text-xs font-semibold px-3 py-1.5 rounded-full">
                    {String(t).trim()}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Visual Identity */}
          <div className="bg-gray-900 p-6 sm:p-8">
            <h2 className="text-sm font-extrabold text-white uppercase tracking-widest mb-6 pb-3 border-b border-gray-700">
              Visual Identity Recommendations
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <InfoRow label="Color Palette" value={primary.colors} />
              <InfoRow label="Typography" value={primary.typography} />
              <InfoRow label="Visual Style" value={primary.visualStyle} />
              <InfoRow label="Tone of Voice" value={primary.toneOfVoice} />
            </div>
          </div>

          {/* Visual Vibes */}
          {data.visual_vibes && data.visual_vibes.length > 0 && (
            <div>
              <div className="text-sm font-extrabold text-[#f5f5f5] uppercase tracking-widest mb-4 pb-2 border-b-2 border-[#e5e5e5]">
                Visual Vibe Preferences
              </div>
              <div className="flex gap-3 flex-wrap">
                {data.visual_vibes.map((v, i) => (
                  <div key={`vibe-${v}`} className="border-2 border-[#e5e5e5] px-6 py-4 text-center min-w-[110px]">
                    <div className="text-3xl font-extrabold text-[#f5f5f5]" style={{ color: '#7A00EB' }}>{i + 1}</div>
                    <div className="text-xs font-bold uppercase tracking-widest text-[#a3a3a3] mt-1">{v}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Archetype breakdown */}
          {data.all_scores && data.all_scores.length > 0 && (
            <div>
              <div className="text-sm font-extrabold text-[#f5f5f5] uppercase tracking-widest mb-4 pb-2 border-b-2 border-[#e5e5e5]">
                Complete Archetype Breakdown
              </div>
              <div className="flex flex-wrap gap-2">
                {data.all_scores
                  .slice()
                  .sort((a, b) => (b.count || 0) - (a.count || 0))
                  .map((s) => (
                    <div key={`score-${s.archetype}`} className="border border-[#404040] px-4 py-3 text-center min-w-[90px]">
                      <div className="text-xl font-extrabold" style={{ color: '#7A00EB' }}>{s.count || 0}</div>
                      <div className="text-xs text-[#9ca3af] font-semibold uppercase tracking-wide mt-0.5">{s.archetype}</div>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="bg-black p-8 sm:p-10 text-center">
            <div className="flex items-center justify-center gap-2 mb-3">
              <Sparkles className="h-5 w-5 text-yellow-400" />
              <span className="text-yellow-400 text-xs font-bold uppercase tracking-widest">Discover Yours</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-3 tracking-tight">
              What's Your Brand Archetype?
            </h2>
            <p className="text-gray-400 text-sm mb-6 max-w-sm mx-auto leading-relaxed">
              Take the free 5-minute quiz based on Carl Jung's personality archetypes and unlock your brand's true identity.
            </p>
            <Button
              onClick={() => navigate('/')}
              className="bg-[#0a0a0a] text-[#f5f5f5] font-extrabold text-sm uppercase tracking-widest px-8 h-12 hover:bg-[#1a1a1a] transition-colors"
              data-testid="take-quiz-cta-btn"
            >
              Take the Free Quiz
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>

        </div>
      </main>
      <Footer />
    </div>
  );
}
